import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauthenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        return { ok: false, requires_2fa: true, temp_token: data.temp_token, email: data.email };
      }
      if (data.access_token) {
        localStorage.setItem("tpco_token", data.access_token);
      }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verify2fa = async (tempToken, code) => {
    try {
      const { data } = await api.post("/auth/2fa/verify", { temp_token: tempToken, code });
      if (data.access_token) {
        localStorage.setItem("tpco_token", data.access_token);
      }
      setUser(data.user);
      return { ok: true, used_backup_code: data.used_backup_code };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("tpco_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, verify2fa }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export const ROLE_PERMS = {
  admin: ["view_all", "edit_all", "delete_all", "manage_users", "config"],
  secretaire: ["view_all", "edit_biens", "edit_clients", "edit_contrats", "register_paiement", "send_rappel"],
  commercial: ["view_biens", "view_clients", "view_contrats", "edit_biens", "edit_clients", "edit_contrats"],
  comptable: ["view_all", "register_paiement", "send_rappel", "export"],
  lecture: ["view_all"],
};

export function can(user, perm) {
  if (!user) return false;
  return (ROLE_PERMS[user.role] || []).includes(perm);
}
